/* eslint-disable no-var */
// Category hub copy, rows and structured data: "Best OTT subscriptions in
// India", one row per brand with its lowest-priced plan in stock, each linking
// to the brand page.
//
// Built from the brand pages src/seo/brandPage.js describes, so a hub never
// lists a brand without a page, and every price on it is one the brand page
// shows too. Shared by the prerender (scripts/prerender-meta.js) and the React
// page at /category/:slug (src/Pages/CategoryPage), like brandPage.js.
//
// CommonJS on purpose: Node requires it directly at build time, and webpack
// and Jest both accept it from the app.
//
// A hub row carries only public fields: the brand's name, web path and logo,
// and the name, duration, Fleek price and list price of one plan, or null when
// nothing is in stock. No cost, supplier, source or stock count.

var brandPage = require('./brandPage');
var currencies = require('../utils/currency');

var SITE_URL = brandPage.SITE_URL;
var money = brandPage.money;

// A hub with one brand is a thin page.
var MIN_HUB_BRANDS = 2;
// "Miscellaneous" is not something anyone searches for.
var SKIP_CATEGORIES = ['miscellaneous'];

// "News & Magazines" -> "news-magazines". The /category/:slug route and the
// prerender both use this one function (src/utils/categorySlug.js re-exports it).
var categorySlug = function (name) {
  return String(name || '')
    .toLowerCase()
    .replace(/&/g, ' ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

// Gaming holds game top-ups, keys and console and store cards: gift cards,
// not subscriptions, like "Gift Cards" itself.
var isGamingCategory = function (name) {
  return /^gaming$/i.test(String(name || '').trim());
};

var isGiftCategory = function (name) {
  return /gift|voucher|game key/i.test(String(name || '')) || isGamingCategory(name);
};

// Gift cards are not subscriptions: "Best OTT subscriptions", "Best Gift
// Cards", "Best Gaming gift cards and top-ups".
// Every store names its country, the way its searches do: "in India" on
// India's hubs, "in the United States" on /us/category/... A store page with
// no country of its own (a listing-only store) names none.
var hubHeading = function (name, abroad, place) {
  var where = place ? ' in ' + place : abroad ? '' : ' in India';
  if (isGamingCategory(name)) return 'Best ' + name + ' gift cards and top-ups' + where;
  return isGiftCategory(name) ? 'Best ' + name + where : 'Best ' + name + ' subscriptions' + where;
};

var clean = function (v) {
  return typeof v === 'string' ? v.trim() : '';
};

/**
 * One hub row from a brand page (buildBrandPage's result). `plan` is the
 * lowest-priced paid plan in stock, in the currency the brand page prices in,
 * which is the plan behind the brand page's "from" price; null when nothing
 * can be bought.
 */
var hubBrandFromPage = function (page, logo) {
  var buyable = (page.plans || []).filter(function (p) {
    return p.inStock && p.price > 0 && p.currency === page.currency;
  });
  var cheapest = buyable.reduce(function (acc, p) {
    return !acc || p.price < acc.price ? p : acc;
  }, null);
  return {
    slug: page.slug,
    name: page.name,
    logo: clean(logo),
    plan: cheapest
      ? {
          name: cheapest.name,
          duration: cheapest.duration || '',
          price: cheapest.price,
          mrp: cheapest.mrp || null,
          currencyType: currencies.currencyType(cheapest.currencyType),
        }
      : null,
  };
};

// Buyable first, rupee prices before any other currency (grouped by
// currency), cheapest first, then by name.
var byPrice = function (a, b) {
  var ap = a.plan;
  var bp = b.plan;
  return (
    Number(!ap) - Number(!bp) ||
    (ap && bp
      ? Number(ap.currencyType !== 'inr') - Number(bp.currencyType !== 'inr') ||
        (ap.currencyType < bp.currencyType ? -1 : ap.currencyType > bp.currencyType ? 1 : 0) ||
        ap.price - bp.price
      : 0) ||
    a.name.localeCompare(b.name)
  );
};

// Subscriptions lead and gift cards go last; bigger hubs first, then by name.
var hubOrder = function (a, b) {
  return (
    Number(isGiftCategory(a.name)) - Number(isGiftCategory(b.name)) ||
    b.brands.length - a.brands.length ||
    a.name.localeCompare(b.name)
  );
};

// Brands people actually buy lead a hub (a searcher for "OTT subscriptions"
// expects Sony LIV and JioHotstar, not the cheapest niche app): ranked brands
// first by rank, then the rest by price as before. `popularity` maps a brand
// slug to its rank (1 = most popular); absent means unranked.
var byPopularityThenPrice = function (popularity) {
  return function (a, b) {
    var ra = popularity && popularity[a.slug];
    var rb = popularity && popularity[b.slug];
    if (ra && rb) return ra - rb;
    if (ra) return -1;
    if (rb) return 1;
    return byPrice(a, b);
  };
};

// Group rows by category, skip thin and "Miscellaneous" hubs, order them.
var groupHubs = function (items, popularity) {
  var map = new Map();
  items.forEach(function (it) {
    var slug = categorySlug(it.category);
    if (!slug || SKIP_CATEGORIES.indexOf(slug) !== -1) return;
    if (!map.has(slug)) map.set(slug, { name: it.category, slug: slug, brands: [] });
    map.get(slug).brands.push(it.row);
  });
  return Array.from(map.values())
    .filter(function (h) {
      return h.brands.length >= MIN_HUB_BRANDS;
    })
    .map(function (h) {
      return { name: h.name, slug: h.slug, brands: h.brands.slice().sort(byPopularityThenPrice(popularity)) };
    })
    .sort(hubOrder);
};

/**
 * Hubs from brand-page entries: [{ page, logo }] where page is a
 * buildBrandPage result. Categories with fewer than two brands are skipped.
 * `popularity` ({ slug: rank }) puts the brands people buy first.
 */
var buildCategoryHubs = function (entries, popularity) {
  return groupHubs(
    (entries || [])
      .filter(function (e) {
        return e && e.page && e.page.category;
      })
      .map(function (e) {
        return { category: e.page.category, row: hubBrandFromPage(e.page, e.logo) };
      }),
    popularity
  );
};

var PERIODS = { mo: 'month', mos: 'month', month: 'month', months: 'month', yr: 'year', yrs: 'year', year: 'year', years: 'year', wk: 'week', wks: 'week', week: 'week', weeks: 'week', day: 'day', days: 'day' };

// "12 mo" → "12 months", "month" → "1 month"; anything else as it came.
var periodLabel = function (text) {
  var m = /^(\d+)?\s*([a-z]+)$/i.exec(String(text || '').trim());
  var unit = m && PERIODS[m[2].toLowerCase()];
  if (!unit) return String(text || '').trim();
  var n = m[1] ? Number(m[1]) : 1;
  return n + ' ' + unit + (n === 1 ? '' : 's');
};

/**
 * A store's cheapest plan for a brand, from the listing's `starts_from`
 * ("$175.99/12 mo", "€9/mo", "CHF 12/mo") and `mrp` ("220.00"): the backend
 * prices it live from that store's own plans, in that store's currency
 * (market.py starts_from), the same plans the brand page lists. Undefined ("unknown") when the listing doesn't say.
 */
var planFromListing = function (b) {
  var label = currencies.parseMoneyLabel(b.starts_from);
  var m = label && /^\s*\/\s*(.+)$/.exec(label.rest);
  if (!m) return undefined;
  var price = label.amount;
  if (!(price > 0)) return undefined;
  var mrp = Number(String(b.mrp || '').replace(/[^0-9.]/g, ''));
  var period = periodLabel(m[1]);
  return {
    name: period,
    duration: period,
    price: price,
    mrp: mrp > price ? mrp : null,
    currencyType: label.currency,
  };
};

/**
 * The same hubs from the marketplace listing (vendor_v2), for the React page
 * when it has no prerendered seed: every store outside India, a category added
 * after the build, or the dev server.
 *
 * With `withPrices` (stores outside India) each row carries the store's
 * cheapest plan from the listing. Otherwise (India's fallback, whose listing
 * figure is cached in any currency) rows have no plan (undefined, "unknown")
 * rather than a guessed one.
 */
var buildListingHubs = function (listing, options) {
  var withPrices = !!(options && options.withPrices);
  return groupHubs(
    (listing || [])
      .filter(function (b) {
        return b && b.vendor_web_path && b.service && b.service.service_name && b.service.category;
      })
      .map(function (b) {
        var row = { slug: b.vendor_web_path, name: String(b.service.service_name).trim(), logo: clean(b.service.logo_url) };
        if (withPrices) row.plan = planFromListing(b);
        return { category: b.service.category, row: row };
      })
  );
};

var listNames = function (names, total) {
  if (!names.length) return '';
  if (total > names.length) return names.join(', ') + ' and more';
  if (names.length === 1) return names[0];
  return names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1];
};

/**
 * Everything a hub page says, from one hub (buildCategoryHubs' shape) and
 * the other hubs to link to. Returns null for a hub with no brands.
 */
var buildCategoryPage = function (hub, options) {
  var opts = options || {};
  var siteUrl = (opts.siteUrl || SITE_URL).replace(/\/$/, '');
  if (!hub || !hub.name || !hub.slug || !Array.isArray(hub.brands) || !hub.brands.length) return null;

  var name = hub.name;
  // opts.country: a country store's own hub (/us/category/ai-tools), whose
  // rows and links stay in that store. India's hubs have no prefix.
  var prefix = brandPage.countryPath(opts.country);
  var place = prefix ? brandPage.placeName(opts.country) : '';
  var url = siteUrl + prefix + '/category/' + hub.slug;
  // opts.abroad: a store outside India (card only, delivered by email within 24 hours).
  var abroad = opts.abroad === true || !!prefix;
  var heading = hubHeading(name, abroad, place);
  var brands = hub.brands;
  var priced = brands.filter(function (b) {
    return b.plan !== undefined;
  });
  var buyable = brands.filter(function (b) {
    return b.plan;
  });
  var names = brands.slice(0, 4).map(function (b) {
    return b.name;
  });

  var title = heading + ': plans and prices | Fleek';
  var metaDescription =
    'Compare ' + name + ' plans and prices on Fleek: ' + listNames(names, brands.length) +
    (abroad ? '. Paid by card, delivered by email within 24 hours.' : '. Paid by UPI, card or wallet, delivered by email and WhatsApp.');

  var count = brands.length + ' ' + name + (brands.length === 1 ? ' brand' : ' brands') + ' on Fleek.';
  var intro;
  if (priced.length === brands.length && buyable.length) {
    intro = count + ' Each row shows the lowest-priced plan you can buy now; open a brand to see every plan and price.';
  } else {
    intro = count + ' Open a brand to see every plan and price.';
  }

  var rows = brands.map(function (b) {
    var p = b.plan;
    return {
      slug: b.slug,
      name: b.name,
      logo: b.logo || '',
      href: prefix + '/brand/' + b.slug,
      known: p !== undefined,
      available: !!p,
      plan: p ? p.name : '',
      duration: p ? p.duration : '',
      price: p ? money(p.price, p.currencyType) : '',
      listPrice: p && p.mrp && p.mrp > p.price ? money(p.mrp, p.currencyType) : '',
    };
  });

  var others = (opts.others || []).filter(function (o) {
    return o && o.slug && o.name && o.slug !== hub.slug;
  });

  var jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Marketplace', item: siteUrl + (prefix || '/marketplace') },
          { '@type': 'ListItem', position: 2, name: name, item: url },
        ],
      },
      {
        '@type': 'CollectionPage',
        '@id': url + '#page',
        name: heading,
        url: url,
        mainEntity: {
          '@type': 'ItemList',
          numberOfItems: brands.length,
          itemListElement: brands.map(function (b, i) {
            return { '@type': 'ListItem', position: i + 1, name: b.name, url: siteUrl + prefix + '/brand/' + b.slug };
          }),
        },
      },
    ],
  };

  return {
    slug: hub.slug,
    url: url,
    country: prefix ? String(opts.country).toUpperCase() : '',
    prefix: prefix,
    name: name,
    heading: heading,
    title: title,
    metaDescription: metaDescription,
    intro: intro,
    rows: rows,
    others: others.map(function (o) {
      return { name: o.name, slug: o.slug };
    }),
    jsonLd: jsonLd,
  };
};

module.exports = {
  MIN_HUB_BRANDS: MIN_HUB_BRANDS,
  categorySlug: categorySlug,
  isGiftCategory: isGiftCategory,
  hubHeading: hubHeading,
  hubBrandFromPage: hubBrandFromPage,
  buildCategoryHubs: buildCategoryHubs,
  buildListingHubs: buildListingHubs,
  planFromListing: planFromListing,
  buildCategoryPage: buildCategoryPage,
};
